import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import _get from 'lodash/get'
import { Map } from 'immutable'
import compose from 'recompose/compose'
import { connect as connectRedux } from 'react-redux'
import { getBoundActions } from '../../actions/index'
import { get as getConfig } from '../../config/index'
import ChevronUp from '../Icon/icons/ChevronUp/index'
import Link from '../Link/index'
import { H1, H2 } from '../Heading/index'
import { selectDefaultOraclePersonalityId } from '../../services/feature-tracking/selectors'
import { EN } from '../../services/languages/constants'
import { getUserLanguagePrimary } from '../../services/user/index'
import { selectFirstName } from '../../services/user/selectors'
import MemberHomeFirstTouchAiConversations from './MemberHomeFirstTouchAiConversations'
import MemberHomeFirstTouchAiInput from './MemberHomeFirstTouchAiInput'
import MemberHomeFirstTouchAiPrompts, {
  MEMBER_HOME_FIRST_TOUCH_AI_PROMPTS_LAYOUT_CENTERED,
} from './MemberHomeFirstTouchAiPrompts'
import MemberHomeFirstTouchTile from './MemberHomeFirstTouchTile'
import useMemberHomeFirstTouchOverlayState from './hooks/useMemberHomeFirstTouchOverlayState'
import {
  MEMBER_HOME_FIRST_TOUCH_MOOD_PROMPT_KEYS,
  selectMemberHomeFirstTouchSectionData,
  getFirstTouchTileContentFromPmData,
  memberHomeFirstTouchRootClassName,
  getColorSchemeForTimeOfDay,
  getHeaderSubtitle,
  getTimeOfDayBackgroundClassName,
  pickRandomTimeOfDayTitle,
  getTabLabel,
  getMemberHomeFirstTouchFormattedContent,
  buildMemberHomeFirstTouchContentGaPayload,
  createMemberHomeFirstTouchRowLabelClickHandler,
  createMemberHomeFirstTouchThumbPlayClickHandler,
} from './utils'

const config = getConfig()
const memberHomeFirstTouchFeatureEnabled = !!_get(config, ['features', 'memberHomeFirstTouch'])

function buildOverlayLayout ({
  data,
  variation,
  greetingTitle,
  timeOfDay,
  staticText,
  visuallyDismissed,
}) {
  const tilesContent = getFirstTouchTileContentFromPmData(data)
  const contentTitle = tilesContent[0]?.getIn(['tabContent', 0, 'content', 0, 'title']) || ''
  return {
    title: greetingTitle,
    subtitle: getHeaderSubtitle({ variation, staticText, contentTitle }),
    tilesContent,
    rootClassName: memberHomeFirstTouchRootClassName({
      dismissed: visuallyDismissed,
      variation,
      timeOfDay,
    }),
    backgroundClassName: getTimeOfDayBackgroundClassName(timeOfDay),
  }
}

function MemberHomeFirstTouch (props) {
  const {
    auth,
    firstTouchSection,
    oracleVoiceAccess,
    userFirstName,
    staticText,
    language,
    pageIsTouch,
    viewportTouchable,
    viewportWidth,
    defaultOraclePersonalityId,
    setMemberHomeFirstTouch,
    setEventDefault,
    setAiGa4Event,
    sendOptimizelyFeatureEvent,
  } = props

  // Viewport gate, PM payload shape, Oracle prefetch, swipe/shell sync for Member Home First Touch
  const overlayState = useMemberHomeFirstTouchOverlayState({
    featureEnabled: memberHomeFirstTouchFeatureEnabled,
    auth,
    firstTouchSection,
    pageIsTouch,
    viewportTouchable,
    viewportWidth,
    defaultOraclePersonalityId,
    setMemberHomeFirstTouch,
  })

  // Overlay state and callbacks
  const {
    overlayRef,
    shouldShowOverlay,
    sectionId,
    data,
    timeOfDay,
    variation,
    showLastAiConversations,
    showAiPrompts,
    showTiles,
    showAiInput,
    recentOracleSessions,
    visuallyDismissed,
    showPullStrip,
    overlayStyle,
    onSlideTouchStart,
    onSlideTouchMove,
    onSlideTouchEnd,
    handleOverlayTransitionEnd,
    pullStripStyle,
    oraclePersonalityId,
  } = overlayState

  const [aiValue, setAiValue] = useState('')

  const [greetingTitle] = useState(() => pickRandomTimeOfDayTitle({
    timeOfDay,
    staticText,
    userFirstName,
  }))

  const layout = useMemo(
    () => (Map.isMap(data)
      ? buildOverlayLayout({
        data,
        variation,
        greetingTitle,
        timeOfDay,
        staticText,
        visuallyDismissed,
      })
      : null),
    [data, variation, greetingTitle, timeOfDay, staticText, visuallyDismissed],
  )

  const hasTiles = layout.tilesContent.length > 0
  if (!shouldShowOverlay || !sectionId || !Map.isMap(data) || layout == null || !hasTiles) {
    return null
  }

  const colorSchemePrefix = getColorSchemeForTimeOfDay(timeOfDay)
  const swipeLabel = staticText.get('swipeToHomepage')

  return (
    <div
      ref={overlayRef}
      className={layout.rootClassName}
      style={overlayStyle}
      aria-hidden={visuallyDismissed}
      onTransitionEnd={handleOverlayTransitionEnd}
    >
      <div className="member-home-first-touch__wrapper">
        <div className={`member-home-first-touch__background ${layout.backgroundClassName}`} />
        <div
          className="member-home-first-touch__panel"
          onTouchStart={onSlideTouchStart}
          onTouchMove={onSlideTouchMove}
          onTouchEnd={onSlideTouchEnd}
          onTouchCancel={onSlideTouchEnd}
        >
          {/* Header */}
          <div className="member-home-first-touch__header">
            <H1 className={`member-home-first-touch__title member-home-first-touch__title--${colorSchemePrefix}`}>
              {layout.title}
            </H1>
            <H2 className={`member-home-first-touch__subtitle member-home-first-touch__subtitle--${colorSchemePrefix}`}>
              {layout.subtitle}
            </H2>
          </div>

          {/* Last AI conversations */}
          {showLastAiConversations && (
            <MemberHomeFirstTouchAiConversations
              auth={auth}
              colorSchemePrefix={colorSchemePrefix}
              sessions={recentOracleSessions}
              staticText={staticText}
            />
          )}

          {/* AI prompts */}
          {showAiPrompts && (
            <MemberHomeFirstTouchAiPrompts
              auth={auth}
              staticText={staticText}
              oraclePersonalityId={oraclePersonalityId}
              colorSchemePrefix={colorSchemePrefix}
              promptKeys={MEMBER_HOME_FIRST_TOUCH_MOOD_PROMPT_KEYS}
              layout={MEMBER_HOME_FIRST_TOUCH_AI_PROMPTS_LAYOUT_CENTERED}
            />
          )}

          {/* Tiles */}
          {showTiles && (
            <div className="member-home-first-touch__tiles">
              {layout.tilesContent.map((tab, rowIndex) => {
                const rawContent = tab.getIn(['tabContent', 0, 'content', 0])
                const tabType = tab.get('tabType')
                const label = getTabLabel(tabType, staticText)
                const recommId = Map.isMap(data) ? data.get('recommId') : undefined
                const formatted = getMemberHomeFirstTouchFormattedContent(rawContent)
                const rowGa = buildMemberHomeFirstTouchContentGaPayload({
                  formatted,
                  rawContent,
                  rowPlacement: rowIndex,
                  rowName: label,
                  recommId,
                })
                const href = rowGa?.href || ''
                const handleRowLabelClick = createMemberHomeFirstTouchRowLabelClickHandler({
                  sendOptimizelyFeatureEvent,
                  setEventDefault,
                  rowGa,
                })
                const handleThumbPlayClick = createMemberHomeFirstTouchThumbPlayClickHandler({
                  sendOptimizelyFeatureEvent,
                  setEventDefault,
                  rowGa,
                })
                const labelClassName = `member-home-first-touch__row-label member-home-first-touch__row-label--${colorSchemePrefix}`
                return (
                  <div className="member-home-first-touch__row" key={tabType}>
                    {href && label
                      ? (
                        <Link
                          className={labelClassName}
                          to={href}
                          onClick={handleRowLabelClick}
                          scrollToTop
                        >
                          {label}
                        </Link>
                      )
                      : (
                        <div className={labelClassName}>
                          {label}
                        </div>
                      )}
                    <MemberHomeFirstTouchTile
                      loading={false}
                      rawContent={rawContent}
                      formattedContent={formatted}
                      contentHref={href ? `${href}?fullplayer=feature` : undefined}
                      onPlayClick={handleThumbPlayClick}
                    />
                  </div>
                )
              })}
            </div>
          )}

          {/* AI input */}
          {showAiInput && (
            <MemberHomeFirstTouchAiInput
              auth={auth}
              staticText={staticText}
              language={language}
              oraclePersonalityId={oraclePersonalityId}
              oracleVoiceAccess={oracleVoiceAccess}
              aiValue={aiValue}
              onAiPromptChange={setAiValue}
              colorSchemePrefix={colorSchemePrefix}
              showSeedPrompts={!showAiPrompts}
              setAiGa4Event={setAiGa4Event}
              sendOptimizelyFeatureEvent={sendOptimizelyFeatureEvent}
            />
          )}

          {/* Slide hint */}
          <span className="member-home-first-touch__slide-hint">
            <ChevronUp
              size={24}
              className={`member-home-first-touch__slide-hint-icon member-home-first-touch__slide-hint-icon--${colorSchemePrefix}`}
            />
            <span className={`member-home-first-touch__slide-hint-text member-home-first-touch__slide-hint-text--${colorSchemePrefix}`}>
              {swipeLabel}
            </span>
          </span>

        </div>
      </div>

      {showPullStrip && (
        <div
          className="member-home-first-touch__pull-strip"
          style={pullStripStyle}
          onTouchStart={onSlideTouchStart}
          onTouchMove={onSlideTouchMove}
          onTouchEnd={onSlideTouchEnd}
          onTouchCancel={onSlideTouchEnd}
          aria-hidden="true"
        />
      )}
    </div>
  )
}

MemberHomeFirstTouch.propTypes = {
  auth: ImmutablePropTypes.map,
  firstTouchSection: PropTypes.object,
  staticText: ImmutablePropTypes.map.isRequired,
  language: PropTypes.string.isRequired,
  pageIsTouch: PropTypes.bool,
  viewportTouchable: PropTypes.bool,
  viewportWidth: PropTypes.number,
  setMemberHomeFirstTouch: PropTypes.func.isRequired,
  defaultOraclePersonalityId: PropTypes.string.isRequired,
  oracleVoiceAccess: PropTypes.bool,
  userFirstName: PropTypes.string,
  setEventDefault: PropTypes.func.isRequired,
  setAiGa4Event: PropTypes.func.isRequired,
  sendOptimizelyFeatureEvent: PropTypes.func.isRequired,
}

MemberHomeFirstTouch.defaultProps = {
  auth: Map(),
  staticText: Map(),
  firstTouchSection: null,
  pageIsTouch: false,
  viewportTouchable: false,
  viewportWidth: 0,
  oracleVoiceAccess: false,
  userFirstName: '',
}

export default compose(
  connectRedux(
    (state) => {
      const { user = Map() } = state
      const auth = Map.isMap(state.auth) ? state.auth : Map()
      const oracle = Map.isMap(state.oracle) ? state.oracle : Map()
      const app = Map.isMap(state.app) ? state.app : Map()
      const language = getUserLanguagePrimary(user) || EN
      return {
        staticText: state.staticText.getIn(['data', 'memberHomeFirstTouch', 'data'], Map()) || Map(),
        defaultOraclePersonalityId: selectDefaultOraclePersonalityId(state),
        firstTouchSection: selectMemberHomeFirstTouchSectionData(state),
        oracleVoiceAccess: oracle.getIn(['access', 'aiSpeech'], false),
        viewportTouchable: !!app.getIn(['viewport', 'touchable']),
        viewportWidth: app.getIn(['viewport', 'width']) || 0,
        pageIsTouch: !!state.page.get('isTouch'),
        userFirstName: selectFirstName(state),
        language,
        auth,
      }
    },
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setMemberHomeFirstTouch: actions.app.setAppMemberHomeFirstTouch,
        setEventDefault: actions.eventTracking.setEventDefault,
        setAiGa4Event: actions.eventTracking.setAiGa4Event,
        sendOptimizelyFeatureEvent: actions.optimizelyFeature.sendOptimizelyFeatureEvent,
      }
    },
  ),
)(MemberHomeFirstTouch)
